import { useEffect } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Keep Tab focus inside `containerRef` while `active`, and hand focus back to
 * whatever held it when the trap engaged. Required for `aria-modal` dialogs:
 * without it, Tab walks into the page behind the sheet, which a screen reader
 * still reports as hidden.
 */
export const useFocusTrap = (containerRef, active) => {
  useEffect(() => {
    const node = containerRef.current;
    if (!active || !node) return;

    const previouslyFocused = document.activeElement;
    // Focus the dialog itself rather than its first control, so the
    // accessible name is announced before the contents.
    node.focus({ preventScroll: true });

    const handleKeyDown = (event) => {
      if (event.key !== 'Tab') return;

      // Skip tabindex="-1" nodes: DayPicker uses a roving tabindex, so all but
      // one day button are unreachable by Tab and must not count as the edge.
      const items = [...node.querySelectorAll(FOCUSABLE)].filter(
        (el) => el.tabIndex >= 0 && (el.offsetWidth > 0 || el.offsetHeight > 0),
      );
      if (items.length === 0) {
        event.preventDefault();
        return;
      }

      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement;

      if (event.shiftKey && (current === first || current === node)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && current === last) {
        event.preventDefault();
        first.focus();
      }
    };

    node.addEventListener('keydown', handleKeyDown);
    return () => {
      node.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocused instanceof HTMLElement)
        previouslyFocused.focus({ preventScroll: true });
    };
  }, [containerRef, active]);
};
